import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Stethoscope, Heart, Brain, Baby, Users, Scissors, Video, Ambulance } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Stethoscope,
  Heart,
  Brain,
  Baby,
  Users,
  Scissors,
  Video,
  Ambulance,
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
    setLoading(false);
  };

  const getIcon = (iconName) => {
    return iconMap[iconName] || Stethoscope;
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#14213D] py-20" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-1.5 bg-[#00B4D8]/20 text-[#00B4D8] text-sm font-medium rounded-full mb-6">
              Our Services
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Comprehensive Healthcare Solutions
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              From primary care to specialized treatments, our network of healthcare 
              providers offers a full range of medical services to meet your needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-[#F1FAEE]" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="bg-white rounded-xl h-64 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => {
                const Icon = getIcon(service.icon);
                return (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 card-hover group"
                    data-testid={`service-${service.id}`}
                  >
                    <div className="w-14 h-14 bg-[#00B4D8]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#00B4D8] transition-colors">
                      <Icon className="w-7 h-7 text-[#00B4D8] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-[#14213D] mb-2">
                      {service.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                    <span className="text-xs font-medium text-[#00B4D8] uppercase tracking-wider">
                      {service.category}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Insurance Section */}
      <section className="py-20 bg-white" data-testid="insurance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#00B4D8] text-sm font-medium uppercase tracking-wider">Insurance</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#14213D] mt-2 mb-6">
                We Accept Most Major Insurance Plans
              </h2>
              <p className="text-gray-600 mb-6">
                Medical Access Inc works with most major insurance providers to ensure 
                you can receive the care you need. Our billing team is available to help verify 
                your coverage and answer any questions.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {["Blue Cross Blue Shield", "Aetna", "Cigna", "UnitedHealthcare", "Humana", "Medicare"].map((insurance) => (
                  <div key={insurance} className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#00B4D8] rounded-full" />
                    <span className="text-gray-700 text-sm">{insurance}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact">
                <Button
                  className="bg-[#14213D] text-white rounded-lg px-6 py-5"
                  data-testid="verify-insurance"
                >
                  Verify Your Coverage
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#F1FAEE] rounded-2xl p-8"
            >
              <h3 className="font-heading font-semibold text-xl text-[#14213D] mb-6">
                Patient Billing Information
              </h3>
              <ul className="space-y-4">
                {[
                  "Transparent pricing for all services",
                  "Flexible payment plans available",
                  "Financial assistance programs",
                  "Online bill payment portal",
                  "Dedicated billing support team",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-600">
                    <div className="w-6 h-6 bg-[#00B4D8]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[#00B4D8] text-xs font-bold">{index + 1}</span>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Telehealth Section */}
      <section className="py-20 bg-[#00B4D8]" data-testid="telehealth-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-white/80 text-sm font-medium uppercase tracking-wider">Virtual Care</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mt-2 mb-6">
                Telehealth Services
              </h2>
              <p className="text-white/90 text-lg mb-8">
                Access quality healthcare from the comfort of your home with our telehealth services. 
                Connect with providers via video for consultations, follow-ups, and more.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Video consultations with licensed providers",
                  "Prescription renewals and management",
                  "Mental health counseling sessions",
                  "Post-operative follow-up visits",
                  "Available 7 days a week",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-white/90">
                    <Video className="w-5 h-5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                className="bg-white text-[#00B4D8] rounded-lg px-6 py-5 hover:bg-gray-100"
                data-testid="start-telehealth"
              >
                Start Virtual Visit
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800"
                alt="Telehealth consultation"
                className="w-full h-80 object-cover rounded-2xl shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#14213D]" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Find a provider in your area and schedule your appointment today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/find-provider">
                <Button
                  className="bg-[#00B4D8] text-white rounded-lg px-6 py-5 hover:bg-[#0096c7]"
                  data-testid="find-provider-cta"
                >
                  Find a Provider
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="border-white text-white rounded-lg px-6 py-5 hover:bg-white/10"
                  data-testid="contact-cta"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
